import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'

const EASE = [0.22, 1, 0.36, 1] as const

// Direct link to the bundled logo ZIP on the marketing CDN. When the ZIP is
// mirrored into /public/downloads/, swap this for the local path so the asset
// lives with the rest of the site.
const LOGO_ZIP_HREF =
  'https://download.monofor.com/branding/monofor-logos-2023-08-16.zip'

const jumpLinks = [
  { label: 'About Monofor', href: '#about' },
  { label: 'Logos', href: '#logos' },
  { label: 'Colors', href: '#colors' },
  { label: 'Typography', href: '#typography' },
  { label: 'Usage', href: '#usage' },
  { label: 'Media contact', href: '#media' },
]

export default function PressHero() {
  return (
    <section className="relative overflow-hidden bg-surface pt-20 pb-16 lg:pt-28 lg:pb-20">
      <BackgroundField />
      <Container size="xl" className="relative">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <Eyebrow tone="brand">Press kit · Brand guidelines</Eyebrow>
          </motion.div>
          <motion.div
            className="mt-6"
            initial={{ y: 24 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
          >
            <Heading as="h1" size="display-xl">
              Logos, colors,
              <br />
              and how to use them.
            </Heading>
          </motion.div>
          <motion.div
            className="mt-7 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE, delay: 0.12 }}
          >
            <Text size="xl" tone="secondary">
              Everything you need to write about, design with, or report on
              Monofor: logos in every approved variant, the brand color
              system, type specimens, and boilerplate text ready to paste.
            </Text>
          </motion.div>
          <motion.div
            className="mt-9 flex flex-wrap items-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE, delay: 0.2 }}
          >
            <Button href={LOGO_ZIP_HREF} variant="primary" size="lg">
              Download logo kit (ZIP) ↓
            </Button>
            <Button href="mailto:press@monofor.com" variant="outline" size="lg">
              Email press@monofor.com
            </Button>
          </motion.div>
          <motion.nav
            aria-label="Press kit sections"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35, ease: EASE, delay: 0.3 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
          >
            {jumpLinks.map((j) => (
              <a
                key={j.href}
                href={j.href}
                className="text-body-sm text-ink-tertiary transition hover:text-ink-primary"
              >
                {j.label}
              </a>
            ))}
          </motion.nav>
        </div>
      </Container>
    </section>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 -right-24 h-[480px] w-[480px] rounded-full bg-brand-100/70 blur-3xl" />
      <div className="absolute top-32 -left-32 h-[320px] w-[320px] rounded-full bg-accent-100/50 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="grid-press-hero" width="56" height="56" patternUnits="userSpaceOnUse">
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-press-hero)" />
      </svg>
    </div>
  )
}
